import {clsx} from 'clsx'
import {memo, useEffect, useMemo, useState} from 'react'

import Activate from './states/Activate'
import activateStyles from './states/Activate.module.css'
import affirmativeStyles from './states/Affirmative.module.css'
import AffirmativeSVG from './states/AffirmativeSVG'
import celebrateStyles from './states/Celebrate.module.css'
import CelebrateSVG from './states/CelebrateSVG'
import commonStyles from './states/Common.module.css'
import confirmStyles from './states/Confirm.module.css'
import ConfirmSVG from './states/ConfirmSVG'
import idleStyles from './states/Idle.module.css'
import IdleSVG from './states/IdleSVG'
import jumpWiggleStyles from './states/JumpWiggle.module.css'
import JumpWiggleSVG from './states/JumpWiggleSVG'
import negativeStyles from './states/Negative.module.css'
import NegativeSVG from './states/NegativeSVG'
import StaticSVG from './states/StaticSVG'
import thinkingStyles from './states/Thinking.module.css'
import ThinkingSVG from './states/ThinkingSVG'
import tickleStyles from './states/Tickle.module.css'
import TickleSVG from './states/TickleSVG'
import userInputStyles from './states/UserInput.module.css'
import UserInputSVG from './states/UserInputSVG'
import {CopilotAnimationState} from './types'

export const CopilotAnimationType = {
  Affirmative: 'affirmative',
  Celebrate: 'celebrate',
  Tickle: 'tickle',
  JumpWiggle: 'jumpWiggle',
  Confirm: 'confirm',
  Idle: 'idle',
  Negative: 'negative',
  Static: 'static',
  Thinking: 'thinking',
  UserInput: 'userInput',
  Activate: 'activate',
} as const

export type CopilotAnimationType = (typeof CopilotAnimationType)[keyof typeof CopilotAnimationType]

type AnimationTime = {
  [key: string]: number
}

const animationTimes: Record<string, AnimationTime> = {
  [CopilotAnimationType.Thinking]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Starting]: 429,
    [CopilotAnimationState.Running]: 1390,
    [CopilotAnimationState.Ending]: 429,
  },
  [CopilotAnimationType.Affirmative]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 627,
  },
  [CopilotAnimationType.Celebrate]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 1056,
  },
  [CopilotAnimationType.Negative]: {
    [CopilotAnimationState.Idle]: 250,
    [CopilotAnimationState.Running]: 792,
  },
  [CopilotAnimationType.Idle]: {
    [CopilotAnimationState.Running]: 13483,
  },
  [CopilotAnimationType.Confirm]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 627,
  },
  [CopilotAnimationType.UserInput]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Starting]: 363,
    [CopilotAnimationState.Running]: 3000,
    [CopilotAnimationState.Ending]: 528,
  },
  [CopilotAnimationType.Tickle]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 1188,
  },
  [CopilotAnimationType.JumpWiggle]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 1254,
  },
  [CopilotAnimationType.Activate]: {
    [CopilotAnimationState.Idle]: 33,
    [CopilotAnimationState.Running]: 2000,
  },
}

interface CopilotAnimationProps {
  ariaLabel?: string
  animationType: CopilotAnimationType
  loopAnimation: boolean
  mode: 'assistive' | 'immersive' | 'task-oriented-assistive'
  onAnimationEnd?: () => void
  /**
   * The size of the animation.
   * Default: 32. Minimum: 16.
   * Suggested sizes are 16, 24, 32, 48, 64, 96, 128.
   */
  size?: number
}

export const CopilotAnimation = memo(function CopilotAnimation({
  ariaLabel,
  animationType,
  onAnimationEnd,
  loopAnimation,
  mode,
  size = 32,
}: CopilotAnimationProps) {
  const [animationState, setAnimationState] = useState<CopilotAnimationState>(CopilotAnimationState.Idle)
  const [runningTime, setRunningTime] = useState<number>(0)
  const computedSize = useMemo(() => (size >= 16 ? size : 16), [size])

  const scale = useMemo(() => (computedSize ? computedSize / 32 : 1), [computedSize])

  const animationTime = animationTimes[animationType]
  useEffect(() => {
    setRunningTime(0)
    setAnimationState(CopilotAnimationState.Idle)
  }, [animationType])

  useEffect(() => {
    if (!animationTime || animationType === CopilotAnimationType.Static) return

    let timeoutId: NodeJS.Timeout
    if (animationState === CopilotAnimationState.Idle) {
      timeoutId = setTimeout(() => {
        setAnimationState(CopilotAnimationState.Starting)
      }, animationTime[CopilotAnimationState.Idle] || 0)
    }

    if (animationState === CopilotAnimationState.Starting) {
      timeoutId = setTimeout(() => {
        setAnimationState(CopilotAnimationState.Running)
        setRunningTime(Date.now())
      }, animationTime[CopilotAnimationState.Starting] || 0)
    }

    if (animationState === CopilotAnimationState.Running && !loopAnimation) {
      const elapsed = Date.now() - runningTime
      const runningAnimationDuration = animationTime[CopilotAnimationState.Running] || 0
      const remainingTime = runningAnimationDuration - (elapsed % runningAnimationDuration)
      timeoutId = setTimeout(() => {
        setAnimationState(CopilotAnimationState.Ending)
      }, remainingTime)
    }

    if (animationState === CopilotAnimationState.Ending) {
      timeoutId = setTimeout(() => {
        setAnimationState(CopilotAnimationState.Idle)
        setRunningTime(0)
        onAnimationEnd?.()
      }, animationTime[CopilotAnimationState.Ending] || 0)
    }

    return () => clearTimeout(timeoutId)

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationState, loopAnimation, animationType])

  const copilotSVG = useMemo(() => {
    const stateToClassMap = {
      [CopilotAnimationState.Idle]: 'idleAnimation',
      [CopilotAnimationState.Starting]: 'startingAnimation',
      [CopilotAnimationState.Running]: 'runningAnimation',
      [CopilotAnimationState.Ending]: 'endingAnimation',
    }

    const stateToClass = (state: CopilotAnimationState, animationStyles: {[key: string]: string}) =>
      animationStyles[stateToClassMap[state]] || ''

    switch (animationType) {
      case CopilotAnimationType.Thinking:
        return (
          <ThinkingSVG ariaLabel={ariaLabel} stateClass={stateToClass(animationState, thinkingStyles)} scale={scale} />
        )
      case CopilotAnimationType.Static:
        return <StaticSVG ariaLabel={ariaLabel} stateClass={stateToClass(animationState, commonStyles)} scale={scale} />
      case CopilotAnimationType.Affirmative:
        return (
          <AffirmativeSVG
            ariaLabel={ariaLabel}
            stateClass={stateToClass(animationState, affirmativeStyles)}
            scale={scale}
          />
        )
      case CopilotAnimationType.Celebrate:
        return (
          <CelebrateSVG
            ariaLabel={ariaLabel}
            stateClass={stateToClass(animationState, celebrateStyles)}
            scale={scale}
          />
        )
      case CopilotAnimationType.Negative:
        return (
          <NegativeSVG ariaLabel={ariaLabel} stateClass={stateToClass(animationState, negativeStyles)} scale={scale} />
        )
      case CopilotAnimationType.Idle:
        return <IdleSVG ariaLabel={ariaLabel} stateClass={stateToClass(animationState, idleStyles)} scale={scale} />
      case CopilotAnimationType.Confirm:
        return (
          <ConfirmSVG ariaLabel={ariaLabel} stateClass={stateToClass(animationState, confirmStyles)} scale={scale} />
        )
      case CopilotAnimationType.UserInput:
        return (
          <UserInputSVG
            ariaLabel={ariaLabel}
            stateClass={stateToClass(animationState, userInputStyles)}
            scale={scale}
          />
        )
      case CopilotAnimationType.Tickle:
        return <TickleSVG ariaLabel={ariaLabel} stateClass={stateToClass(animationState, tickleStyles)} scale={scale} />
      case CopilotAnimationType.JumpWiggle:
        return (
          <JumpWiggleSVG
            ariaLabel={ariaLabel}
            stateClass={stateToClass(animationState, jumpWiggleStyles)}
            scale={scale}
          />
        )
      case CopilotAnimationType.Activate:
        return (
          <Activate ariaLabel={ariaLabel} stateClass={stateToClass(animationState, activateStyles)} scale={scale} />
        )
    }
  }, [ariaLabel, animationType, animationState, scale])

  return (
    <div
      className={clsx(
        commonStyles.copilotAnimationHolder,
        mode === 'assistive' && commonStyles.assistive,
        animationType === CopilotAnimationType.Activate && commonStyles.activate,
      )}
      style={{'--copilot-animation-scale': scale} as React.CSSProperties}
    >
      <div
        className={clsx(
          commonStyles.copilotAnimation,
          animationType === CopilotAnimationType.Activate && commonStyles.activate,
        )}
      >
        {copilotSVG}
      </div>
    </div>
  )
})

try{ CopilotAnimation.displayName ||= 'CopilotAnimation' } catch {}